import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { api } from "../../lib/api";
import type { CurrentUser } from "../../lib/auth";
import { CodeEditor } from "../common/LazyCodeEditor";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ExpressionEditor } from "../common/LazyExpressionEditor";
import { SectionEditor, growStyle } from "./SectionEditor";
import { ModelSelector, type ModelSelectorValue } from "./ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback } from "./ModelFallbackSection";
import { DialogPanel } from "../shared/DialogPanel";
import { CheckIcon, XIcon } from "../shared/Icons";
import { toastError } from "../shared/toast";
import { backdropClose } from "../shared/backdrop";
import { EditableFieldsPicker, GlobalToolEditor, GlobalToolsPanel, MODEL_PATHS, VersionBadge, globalTag } from "./SelfService";
import type { Channel } from "./ConfigPrompt";

const _authedRoute = getRouteApi("/_authed");

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
};

function parseCurl(curl: string): Partial<ToolForm> {
  // Ignora o que vem depois de um pipe shell (ex.: | jq -r '.markdown').
  const text = curl.split(/\s\|\s/)[0];
  const result: Partial<ToolForm> = { method: "GET", url: "", headers: "{}", params: "{}", body: "{}" };

  // URL (com ou sem aspas) — separa a query string para a seção Query.
  const urlMatch = text.match(/(['"]?)(https?:\/\/[^'"\s]+)\1/);
  if (urlMatch) {
    const raw = urlMatch[2];
    const qIdx = raw.indexOf("?");
    if (qIdx >= 0) {
      result.url = raw.slice(0, qIdx);
      const params: Record<string, string> = {};
      for (const pair of raw.slice(qIdx + 1).split("&")) {
        if (!pair) continue;
        const eq = pair.indexOf("=");
        const k = eq >= 0 ? pair.slice(0, eq) : pair;
        const v = eq >= 0 ? pair.slice(eq + 1) : "";
        try { params[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, " ")); }
        catch { params[k] = v; }
      }
      result.params = JSON.stringify(params, null, 2);
    } else {
      result.url = raw;
    }
  }

  // Headers: -H / --header, aspas simples ou duplas.
  const headers: Record<string, string> = {};
  for (const m of text.matchAll(/(?:-H|--header)\s+(['"])([\s\S]*?)\1/g)) {
    const idx = m[2].indexOf(":");
    if (idx >= 0) headers[m[2].slice(0, idx).trim()] = m[2].slice(idx + 1).trim();
  }
  if (Object.keys(headers).length) result.headers = JSON.stringify(headers, null, 2);

  // Body: -d / --data / --data-raw / --data-binary  → implica POST.
  const bodyMatch = text.match(/(?:-d|--data(?:-raw|-binary)?)\s+(['"])([\s\S]*?)\1/);
  if (bodyMatch) { result.body = bodyMatch[2]; result.method = "POST"; }
  // --data-urlencode "k=v" (N vezes) e -d "a=b&c=d" (form) → objeto {k: v}.
  const pairs: Record<string, string> = {};
  for (const m of text.matchAll(/--data-urlencode\s+(['"])([\s\S]*?)\1/g)) {
    const idx = m[2].indexOf("=");
    if (idx > 0) pairs[m[2].slice(0, idx).trim()] = m[2].slice(idx + 1);
  }
  if (!Object.keys(pairs).length && bodyMatch && !bodyMatch[2].trim().startsWith("{")) {
    for (const kv of bodyMatch[2].split("&")) {
      const idx = kv.indexOf("=");
      if (idx > 0) pairs[decodeURIComponent(kv.slice(0, idx))] = decodeURIComponent(kv.slice(idx + 1));
    }
  }
  if (Object.keys(pairs).length) { result.body = JSON.stringify(pairs, null, 2); result.method = "POST"; }

  // Método explícito (-X / --request) vence.
  const methodMatch = text.match(/(?:-X|--request)\s+(['"]?)(\w+)\1/);
  if (methodMatch) result.method = methodMatch[2].toUpperCase();

  return result;
}

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
}

interface ToolForm {
  name: string;
  description: string;
  kind: "http" | "python";
  method: string;
  url: string;
  headers: string;
  params: string;
  body: string;
  timeout: number | null;
  code: string;
  model_provider_override: string | null;
  model_name_override: string | null;
  model_temperature_override: number | null;
  model_reasoning_effort_override: string | null;
  model_fallback: ModelSelectorValue | null;
  prompts: Record<string, string>;
  paramDescriptions: Record<string, string>;
  param_values: Record<string, string>;
  // Modo de cada param do run(): true = Fixo, ausente/false = Agente. Guardado à
  // parte pra alternar sem apagar o texto do outro lado (descrição vs valor).
  fixoParams: Record<string, boolean>;
  cooldown_seconds: number | null;
  cooldown_message: string;
  qualifies_human: boolean;
  ends_followup: boolean;
  pause_minutes: number | null;
  template_description: string;
  editable_fields: string[];  // modelo global: campos liberados pro gestor
}

const DEFAULT_FORM: ToolForm = {
  name: "",
  description: "",
  kind: "http",
  method: "POST",
  url: "",
  headers: "{}",
  params: "{}",
  body: "{}",
  timeout: null,
  code: "",
  model_provider_override: null,
  model_name_override: null,
  model_temperature_override: null,
  model_reasoning_effort_override: null,
  model_fallback: null,
  prompts: {},
  paramDescriptions: {},
  param_values: {},
  fixoParams: {},
  cooldown_seconds: null,
  cooldown_message: "",
  qualifies_human: false,
  ends_followup: false,
  pause_minutes: null,
  template_description: "",
  editable_fields: [],
};

function formFromTool(tool: any): ToolForm {
  if (!tool) return DEFAULT_FORM;
  return {
    name: tool.name ?? "",
    description: tool.description ?? "",
    kind: tool.kind === "python" ? "python" : "http",
    method: tool.method ?? "POST",
    url: tool.url ?? "",
    headers: JSON.stringify(tool.headers ?? {}, null, 2),
    params: JSON.stringify(tool.params ?? {}, null, 2),
    body: typeof tool.body === "string" ? tool.body : JSON.stringify(tool.body ?? {}, null, 2),
    timeout: typeof tool.timeout === "number" ? tool.timeout : null,
    code: tool.code ?? DEFAULT_FORM.code,
    model_provider_override: tool.model_provider_override ?? null,
    model_name_override: tool.model_name_override ?? null,
    model_temperature_override: typeof tool.model_temperature_override === "number" ? tool.model_temperature_override : null,
    model_reasoning_effort_override: tool.model_reasoning_effort_override ?? null,
    model_fallback: fallbackToForm(tool.model_fallback),
    prompts: (tool.prompts && typeof tool.prompts === "object") ? tool.prompts : {},
    paramDescriptions: (() => {
      const ps = tool.params_schema && typeof tool.params_schema === "object" ? tool.params_schema : {};
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(ps as Record<string, any>)) {
        if (typeof v?.description === "string") out[k] = v.description;
      }
      return out;
    })(),
    param_values: (tool.param_values && typeof tool.param_values === "object") ? tool.param_values : {},
    fixoParams: Object.fromEntries(Object.keys((tool.param_values && typeof tool.param_values === "object") ? tool.param_values : {}).map((k) => [k, true])),
    cooldown_seconds: typeof tool.cooldown_seconds === "number" ? tool.cooldown_seconds : null,
    cooldown_message: tool.cooldown_message ?? "",
    qualifies_human: tool.qualifies_human ?? false,
    ends_followup: tool.ends_followup ?? false,
    pause_minutes: typeof tool.pause_minutes === "number" && tool.pause_minutes > 0 ? tool.pause_minutes : null,
    template_description: tool.template_description ?? "",
    editable_fields: Array.isArray(tool.editable_fields) ? tool.editable_fields : [],
  };
}

function aiDescFromTool(tool: any): Record<string, string> {
  const ps = tool?.params_schema && typeof tool.params_schema === "object" && tool.kind !== "python" ? tool.params_schema : {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(ps as Record<string, any>)) {
    out[k] = typeof v?.description === "string" ? v.description : "";
  }
  return out;
}

// "Copiar" tool: JSON pra colar em "Nova ferramenta → Colar" de outro agente, sem segredos.
const SECRET_PLACEHOLDER = "<id ou key>";
// ponytail: casa por substring do nome ("keyword" também cai); trocar por lista exata se incomodar.
const SECRET_NAMES = "authorization|token|key|secret|password|senha|chave|bearer|cookie";
const SECRET_NAME_RE = new RegExp(SECRET_NAMES, "i");
// Código Python: nome = "v", nome: str = "v", "nome": "v", x["nome"] = "v" (f-string e variáveis ficam).
const CODE_SECRET_RE = new RegExp(`((["']?)[\\w-]*(?:${SECRET_NAMES})[\\w-]*\\2\\]?\\s*(?::\\s*\\w+\\s*=|[:=])\\s*)(["'])[^"'\\n]+\\3`, "gi");
const CODE_AUTH_RE = /(["'])(Bearer|Basic)\s+[^"'\s{]+\1/gi;  // "Bearer abc" literal em qualquer lugar do código
const URL_SECRET_RE = new RegExp(`([?&][^=&#]*(?:${SECRET_NAMES})[^=&#]*=)(?!\\{\\{|\\[\\[)[^&#]+`, "gi");
const AUTH_VALUE_RE = /^(bearer|basic)\s+\S/i;

function maskSecretValue(v: unknown): unknown {
  // Só string é segredo (max_tokens: 800 fica); var {{ }} / param da IA [[ ]] também ficam.
  if (typeof v !== "string" || v === "" || /\{\{|\[\[/.test(v)) return v;
  const auth = /^(bearer|basic)\s/i.exec(v);
  return auth ? `${auth[1]} ${SECRET_PLACEHOLDER}` : SECRET_PLACEHOLDER;
}

function maskSecrets(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(maskSecrets);
  if (v && typeof v === "object") {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [
      k, SECRET_NAME_RE.test(k) && (x === null || typeof x !== "object") ? maskSecretValue(x) : maskSecrets(x),
    ]));
  }
  if (v === "***") return SECRET_PLACEHOLDER;  // API já mascarou (usuário sem acesso aos segredos)
  return typeof v === "string" && AUTH_VALUE_RE.test(v) ? maskSecretValue(v) : v;  // "Bearer x" com nome qualquer
}

function toolToClipboard(tool: any): string {
  const t = {
    name: tool.name, description: tool.description, kind: tool.kind,
    method: tool.method, timeout: tool.timeout,
    url: typeof tool.url === "string" ? tool.url.replace(URL_SECRET_RE, `$1${SECRET_PLACEHOLDER}`) : tool.url,
    headers: maskSecrets(tool.headers ?? {}), params: maskSecrets(tool.params ?? {}), body: maskSecrets(tool.body ?? {}),
    code: typeof tool.code === "string"
      ? tool.code.replace(CODE_SECRET_RE, `$1$3${SECRET_PLACEHOLDER}$3`).replace(CODE_AUTH_RE, `$1$2 ${SECRET_PLACEHOLDER}$1`)
      : tool.code,
    params_schema: tool.params_schema, param_values: maskSecrets(tool.param_values ?? {}), prompts: tool.prompts,
    model_provider_override: tool.model_provider_override, model_name_override: tool.model_name_override,
    model_temperature_override: tool.model_temperature_override, model_reasoning_effort_override: tool.model_reasoning_effort_override,
    model_fallback: tool.model_fallback,
    cooldown_seconds: tool.cooldown_seconds, cooldown_message: tool.cooldown_message,
    qualifies_human: tool.qualifies_human, ends_followup: tool.ends_followup, pause_minutes: tool.pause_minutes,
  };
  return JSON.stringify(t, null, 2);
}

function scanAiParamsSchema(form: ToolForm, aiDesc: Record<string, string>): Record<string, unknown> | null {
  const text = `${form.url}\n${form.params}\n${form.body}`;
  const names = new Set<string>();
  for (const m of text.matchAll(/\[\[\s*(\w+)\s*\]\]/g)) names.add(m[1]);
  if (names.size === 0) return null;
  const out: Record<string, unknown> = {};
  for (const n of names) out[n] = { type: "string", description: aiDesc[n] ?? "" };
  return out;
}

// Acha as chaves prompts["x"] usadas no código → cada uma vira uma caixa editável.
function scanPromptKeys(code: string): string[] {
  const keys = new Set<string>();
  for (const m of code.matchAll(/prompts\[\s*(['"])([^'"]+)\1\s*\]/g)) keys.add(m[2]);
  return [...keys];
}

function buildPrompts(form: ToolForm): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of scanPromptKeys(form.code)) out[k] = form.prompts[k] ?? "";
  return out;
}

// Extrai os nomes dos parâmetros de `async def run(...)` (exceto ctx), respeitando
// colchetes (ex.: Annotated[str, "a, b"] não é dividido na vírgula interna).
function scanRunParams(code: string): string[] {
  const m = code.match(/async\s+def\s+run\s*\(/);
  if (!m || m.index == null) return [];
  let depth = 1;
  let sig = "";
  for (let i = m.index + m[0].length; i < code.length && depth > 0; i++) {
    const ch = code[i];
    if (ch === "(" || ch === "[" || ch === "{") depth++;
    else if (ch === ")" || ch === "]" || ch === "}") { depth--; if (depth === 0) break; }
    sig += ch;
  }
  const parts: string[] = [];
  let d = 0, cur = "";
  for (const ch of sig) {
    if (ch === "[" || ch === "(" || ch === "{") d++;
    else if (ch === "]" || ch === ")" || ch === "}") d--;
    if (ch === "," && d === 0) { parts.push(cur); cur = ""; }
    else cur += ch;
  }
  if (cur.trim()) parts.push(cur);
  const names: string[] = [];
  for (const p of parts) {
    const t = p.trim();
    if (!t || t.startsWith("*")) continue;
    const name = t.match(/^(\w+)/)?.[1];
    if (name && name !== "ctx") names.push(name);
  }
  return names;
}

function buildParamDescriptions(form: ToolForm): Record<string, string> {
  const out: Record<string, string> = {};
  // Só params "Agente" (não-Fixo). Fixo vive em param_values; sem esse guard o mesmo
  // param iria pros dois lugares (quebra a partição Agente/Fixo).
  for (const p of scanRunParams(form.code)) {
    if (!form.fixoParams[p]) out[p] = form.paramDescriptions[p] ?? "";
  }
  return out;
}

// Só params Fixo (em param_values) que ainda existem na assinatura do run().
function buildParamValues(form: ToolForm): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const p of scanRunParams(form.code)) {
    if (form.fixoParams[p]) out[p] = form.param_values[p] ?? "";
  }
  return Object.keys(out).length ? out : null;
}

// Variáveis globais que a tool usa: vars["x"] / ctx["vars"]["x"] no código e
// {{x}} em valores Fixo (param_values) e em prompts["chave"].
function scanUsedVars(form: ToolForm): string[] {
  const names = new Set<string>();
  // waba.*/lead.* são resolvidos pelo sistema (canal do agente/lead da conversa)
  // — nunca pedir valor no popup de teste.
  const reserved = new Set(["agente", "contexto", "wh", "waba", "lead"]);
  for (const m of form.code.matchAll(/\bvars\[\s*["']([^"']+)["']\s*\]/g)) names.add(m[1]);
  for (const m of form.code.matchAll(/\[\s*["']vars["']\s*\]\s*\[\s*["']([^"']+)["']\s*\]/g)) names.add(m[1]);
  const texts = [
    ...scanRunParams(form.code).filter((p) => form.fixoParams[p]).map((p) => form.param_values[p] ?? ""),
    ...Object.values(form.prompts),
  ];
  for (const t of texts) {
    // Nome completo, com caminho pontilhado ({{ teste.leads }} → "teste.leads").
    for (const m of String(t ?? "").matchAll(/\{\{\s*([A-Za-z_]\w*(?:\.\w+)*)/g)) names.add(m[1]);
  }
  return [...names].filter((n) => !reserved.has(n.split(".")[0]));
}

const TOOL_GUIDE = `Gere SOMENTE o código de uma função Python "async def run(...)" para a plataforma RevendAI — atendimento ao cliente da própria empresa via API oficial do WhatsApp Business (Cloud API). Prefira código simples e curto.

COMO FUNCIONA
- A tool roda num sandbox quando o agente (atendente de WhatsApp) decide chamá-la numa conversa.
- Ela recebe parâmetros configurados na interface, executa, e RETORNA UMA STRING que volta pro agente usar na resposta.
- A tool NÃO recebe o histórico da conversa.

ASSINATURA
- async def run(<parametros>, *, ctx): sempre async; ctx é keyword-only (NUNCA liste ctx como entrada).
- type hints simples: nome: str (ou int/float/bool). Sem default = obrigatório; com default (=10) = opcional.
- NÃO descreva o parâmetro no código — a descrição é editada numa caixa na interface. Deixe o parâmetro enxuto.
- Parâmetros SEMPRE chegam como str. Converta quando precisar: int(x), json.loads(x).
- A função DEVE retornar string. Dados estruturados: json.dumps(obj, ensure_ascii=False).

TIPOS DE PARÂMETRO (o operador escolhe na interface — você só declara no run()):
- Agente: o agente preenche pela conversa (ex.: criterio = o que o cliente pediu). Use pro que depende do cliente.
- Fixo: valor fixo configurado na interface (URL, token, id, lista). Use pra configuração — NUNCA hardcode isso no código.
- IA: NÃO é parâmetro. É prompts["chave"] chamado no código com await ai(prompts["chave"] + dados). Use quando a IA precisa reagir a algo que o código buscou (ex.: resultado de um http.get).

DISPONÍVEL NO CÓDIGO (sem import; json, re etc. liberados):
- await ai(prompt) -> chama a IA, retorna texto. Valide a saída (o modelo pode vir com texto extra).
- http -> httpx: await http.get(url), await http.post(url, json=...). Use .json() ou .text. Trate erro: if r.status_code >= 400: return "erro: " + r.text[:300].
- prompts["chave"] -> texto de prompt editado na interface (aceita {{variavel_global}}, chega renderizado).
- vars["minha_var"] -> variáveis globais do sistema (use direto).
- ctx["contexto"] -> dados da conversa (ex.: ctx["contexto"]["numero"]). ctx["agente"] -> {nome, id}.
- now -> datetime.now. Horário de SP: now(timezone(timedelta(hours=-3))).
- Data/hora BRL já formatada (ex.: "quinta, 14-08-2026 15:30h" — mesmo formato do {{now_brl()}} dos prompts): from carmillion_core.templating import now_brl_str -> now_brl_str(). PREFIRA isso a formatar na mão.

NATIVO NA PLATAFORMA (já existe na interface da tool — se pedirem, avise que se configura lá e NÃO reimplemente em código):
- Limite de uso por cliente ("já enviei?", "só 1 vez por cliente", cooldown): campo "Limite de uso (por cliente)", com mensagem customizada, limpo pelo #reset. Trava manual em kv não é limpa pelo #reset e fica presa pra sempre.
- Timeout: campo Timeout da tool (padrão 60s). Não implemente timeout/retry no código.

MEMÓRIA (kv, Redis) — só quando precisar lembrar algo ENTRE chamadas (contador, rodízio, cache):
- await kv.get(chave)->str|None, kv.set(chave, valor), kv.setex(chave, valor, ttl_seg), kv.incr(chave)->int, kv.delete(chave).
- Valores são string (json.dumps/loads p/ estruturas). A chave é isolada por agente automaticamente — escreva só a chave curta (ex.: "rodizio:vendedor"), NÃO inclua o id do agente.
- Contador/rodízio: use SEMPRE kv.incr (atômico — dois leads ao mesmo tempo pegam números diferentes). NUNCA get-depois-set p/ incrementar.

LIMITES: loops curtos (timeout é o da interface). Sem banco/env/arquivo — integração = http, memória = kv, config = parâmetros/vars, prompt = prompts. print() vai só pros logs de teste.

EXEMPLO (criterio=Agente, url_base=Fixo, prompts["selecionar"/"apresentar"]=IA):
import json, re

async def run(url_base: str, criterio: str = "", *, ctx):
    base = url_base.rstrip("/")
    veiculos = (await http.get(base + "/list")).json()
    escolha = await ai(prompts["selecionar"] + "\\n\\nEstoque: " + json.dumps(veiculos, ensure_ascii=False) + "\\n\\nCliente quer: " + (criterio or "os mais vendáveis"))
    ids = re.findall(r"\\d+", escolha)
    if not ids:
        return "Nada no estoque agora."
    fichas = (await http.get(base + "/api/data?id=" + ",".join(ids))).json()
    return await ai(prompts["apresentar"] + "\\n\\n" + json.dumps(fichas, ensure_ascii=False))

EXEMPLO (memória, rodízio atômico — vendedores=Fixo):
async def run(vendedores: str, *, ctx):
    lista = json.loads(vendedores)
    n = await kv.incr("rodizio:vendedor")
    return lista[(n - 1) % len(lista)]

Gere apenas o código Python da função run, sem explicações.`;

export function ToolModal({
  agentId, tool, initialKind, initialStep, onClose, templateMode = false, role = "agent", initialGlobal = false,
  channel = "whatsapp",
}: {
  agentId?: string;
  tool?: any;
  initialKind?: "http" | "python";
  initialStep?: "curl" | "form";
  onClose: () => void;
  templateMode?: boolean;
  role?: "agent" | "followup_end";
  initialGlobal?: boolean;
  channel?: Channel;  // sub-aba onde a tool nasce (fixo; edição não muda o canal)
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<"curl" | "form">(tool ? "form" : (initialStep ?? "form"));
  const [curlText, setCurlText] = useState("");
  const [curlModal, setCurlModal] = useState(false);
  const [curlError, setCurlError] = useState("");
  const [curlImportKey, setCurlImportKey] = useState(0);  // bump → remonta os SectionEditor após import
  const [form, setForm] = useState<ToolForm>(() => {
    const base = formFromTool(tool);
    return tool ? base : { ...base, kind: initialKind ?? "http" };
  });
  const [aiDesc, setAiDesc] = useState<Record<string, string>>(() => aiDescFromTool(tool));
  // dirty = mudou algo desde que abriu? (só pede confirmação de descarte se sim)
  const snapRef = useRef<string | null>(null);
  const snapshot = JSON.stringify({ form, aiDesc });
  if (snapRef.current === null) snapRef.current = snapshot;
  const dirty = snapshot !== snapRef.current;

  // Catálogo de modelos (mesma query da config do agente) e providers com chave cadastrada.
  const { data: modelCatalog = [] } = useQuery<AvailableModel[]>({
    queryKey: ["available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });
  const { data: apiKeys = [], isSuccess: apiKeysLoaded } = useQuery<{ provider: string }[]>({
    queryKey: ["api-keys"],
    queryFn: () => api.get("/api-keys"),
  });
  const providersWithKey = new Set(apiKeys.map((k) => k.provider));

  // Seletor de modelo aparece quando o código python chama ai() (qualquer
  // prompts["chave"] implica um ai()). Override on = provider E name setados.
  const showModelSelector = form.kind === "python" && form.code.includes("ai(");
  // Tool global (Self-Service): modelo com checkbox "Editável" por campo.
  const globalMode = templateMode && (initialGlobal || Array.isArray(tool?.editable_fields));
  const editableCandidates = (() => {
    const c: { path: string; label: string; paths?: string[] }[] = [];
    const keys = (json: string) => { try { return Object.keys(JSON.parse(json) || {}); } catch { return []; } };
    c.push({ path: "name", label: "Nome" }, { path: "description", label: "Descrição" });
    if (form.kind === "http") {
      c.push({ path: "url", label: "URL" }, { path: "method", label: "Método" });
      c.push({ path: "headers", label: "Headers (todos)" }, { path: "params", label: "Params (todos)" }, { path: "body", label: "Body (todo)" });
      for (const k of keys(form.headers)) c.push({ path: `headers.${k}`, label: `Header ${k}` });
      for (const k of keys(form.params)) c.push({ path: `params.${k}`, label: `Param ${k}` });
      for (const k of keys(form.body)) c.push({ path: `body.${k}`, label: `Body ${k}` });
      for (const k of Object.keys(scanAiParamsSchema(form, aiDesc) ?? {})) c.push({ path: `params_schema.${k}`, label: `Param ${k} (descrição p/ IA)` });
    } else {
      c.push({ path: "code", label: "Código Python" });
      for (const k of Object.keys(form.fixoParams).filter((k) => form.fixoParams[k])) c.push({ path: `param_values.${k}`, label: `Fixo ${k}` });
      for (const k of scanPromptKeys(form.code)) c.push({ path: `prompts.${k}`, label: `Prompt ${k}` });
      // Params "Agente": o que o gestor ajusta é a descrição que a IA lê pra preencher.
      for (const k of scanRunParams(form.code).filter((k) => !form.fixoParams[k])) c.push({ path: `params_schema.${k}`, label: `Param ${k} (descrição p/ IA)` });
      if (form.code.includes("ai(")) c.push({ path: "model", label: "Modelo da IA (provider, modelo, temperatura, fallback)", paths: MODEL_PATHS });
    }
    c.push(
      { path: "timeout", label: "Timeout" },
      { path: "cooldown_seconds", label: "Limite de uso (cooldown)" }, { path: "cooldown_message", label: "Mensagem de cooldown" },
      { path: "qualifies_human", label: "Qualifica para humano" }, { path: "ends_followup", label: "Encerra follow-up" },
      { path: "pause_minutes", label: "Pausa da IA após responder" },
    );
    return c;
  })();
  // Tool global existente: salvar = publicar em todo agente vinculado → pede a nota da versão.
  const [noteOpen, setNoteOpen] = useState(false);
  const [versionNote, setVersionNote] = useState("");
  const overrideOn = form.model_provider_override !== null && !!form.model_name_override;
  // Aviso só depois que /api-keys carregou, senão dá falso-positivo durante o load.
  const overrideMissingKey =
    apiKeysLoaded && showModelSelector && overrideOn &&
    !providersWithKey.has(form.model_provider_override ?? "");

  // Tela "Colar": JSON copiado de outra tool (botão Copiar) ou comando cURL.
  function handleImport() {
    const text = curlText.trim();
    if (text.startsWith("{")) {
      let t: any;
      try { t = JSON.parse(text); } catch { setCurlError("JSON inválido. Cole o texto copiado pelo botão Copiar da ferramenta."); return; }
      setForm(formFromTool(t));
      setAiDesc(aiDescFromTool(t));
    } else if (/^curl\b/i.test(text)) {
      setForm((prev) => ({ ...prev, ...parseCurl(text) }));
    } else {
      setCurlError("Não reconheci. Cole o JSON copiado de uma ferramenta (começa com {) ou um comando cURL (começa com curl).");
      return;
    }
    setStep("form");
  }
  // Campos que ainda têm o placeholder dos segredos removidos na cópia.
  const pendingSecrets = ([
    ["URL", form.url], ["Headers", form.headers], ["Query", form.params], ["Body", form.body],
    ["Código", form.code], ["Parâmetros fixos", JSON.stringify(form.param_values)],
  ] as const).filter(([, v]) => v.includes(SECRET_PLACEHOLDER)).map(([label]) => label);

  // Reimporta um cURL por cima da tool HTTP atual (reseta método/url/headers/query/body).
  function importCurlOverwrite() {
    const text = curlText.trim();
    if (!/^\s*curl\b/i.test(text)) { setCurlError("Cole um comando cURL (deve começar com \"curl\")."); return; }
    const parsed = parseCurl(text);
    if (!parsed.url || !/^https?:\/\//i.test(parsed.url)) { setCurlError("Não achei uma URL http(s) válida no cURL."); return; }
    setForm((prev) => ({
      ...prev,
      method: parsed.method ?? "GET",
      url: parsed.url ?? "",
      headers: parsed.headers ?? "{}",
      params: parsed.params ?? "{}",
      body: parsed.body ?? "{}",
    }));
    setCurlImportKey((k) => k + 1);  // força os SectionEditor a relerem o novo valor
    setCurlText("");
    setCurlError("");
    setCurlModal(false);
  }

  function payload() {
    return {
      name: form.name,
      description: form.description,
      role,
      channel,
      kind: form.kind,
      method: form.method,
      url: form.kind === "http" ? form.url : null,
      headers: (() => { try { return JSON.parse(form.headers); } catch { return {}; } })(),
      params: form.kind === "http" ? (() => { try { return JSON.parse(form.params); } catch { return {}; } })() : {},
      body: (() => { try { return JSON.parse(form.body); } catch { return form.body; } })(),
      // form-urlencoded no header → executor manda como form (data=), não JSON.
      body_type: /x-www-form-urlencoded/i.test(form.headers) ? "form" : "json",
      timeout: form.timeout,
      code: form.kind === "python" ? form.code : null,
      params_schema: form.kind === "python" ? null : scanAiParamsSchema(form, aiDesc),
      param_descriptions: form.kind === "python" ? buildParamDescriptions(form) : null,
      param_values: form.kind === "python" ? buildParamValues(form) : null,
      model_provider_override: showModelSelector && overrideOn ? form.model_provider_override : null,
      model_name_override: showModelSelector && overrideOn ? form.model_name_override : null,
      model_temperature_override: showModelSelector && overrideOn ? form.model_temperature_override : null,
      model_reasoning_effort_override: showModelSelector && overrideOn ? form.model_reasoning_effort_override : null,
      // Sem override o fallback é o do agente — não faz sentido guardar aqui.
      model_fallback: showModelSelector && overrideOn ? formToFallback(form.model_fallback) : null,
      prompts: form.kind === "python" ? buildPrompts(form) : {},
      cooldown_seconds: form.cooldown_seconds,
      cooldown_message: form.cooldown_message.trim() || null,
      qualifies_human: form.qualifies_human,
      ends_followup: form.ends_followup,
      pause_minutes: form.pause_minutes || null,
      ...(templateMode ? { template_description: form.template_description.trim() || null, editable_fields: globalMode ? form.editable_fields : null } : {}),
      ...(globalMode && tool ? { version_note: versionNote.trim() } : {}),
    };
  }

  // Modelos de tool vivem em /tool-templates (tabela dedicada); tools de agente em /agents/{id}/tools.
  const createPath = templateMode ? "/tool-templates" : `/agents/${agentId}/tools`;
  const updatePath = templateMode ? `/tool-templates/${tool?.id}` : `/agents/${agentId}/tools/${tool?.id}`;
  const invalidateKey = templateMode ? ["tool-templates"] : ["agent-tools", agentId];

  const [formError, setFormError] = useState("");
  const saveError = (e: any) => setFormError(e?.payload?.detail ?? "Erro ao salvar a ferramenta.");
  // Publicar como modelo do sistema: Admin ou permissão tool_publicar; só em tool existente de agente.
  const _user = (_authedRoute.useRouteContext() as { user?: CurrentUser }).user;
  const canPublish = !!tool && !templateMode &&
    (_user?.role_name === "Admin" || !!_user?.permissions?.tool_publicar);
  const [pubMsg, setPubMsg] = useState("");
  const publishMut = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/tools/${tool?.id}/publish`, {}),
    onSuccess: () => { setPubMsg("✓ publicado no catálogo"); qc.invalidateQueries({ queryKey: ["tool-templates"] }); },
    onError: (e: any) => setPubMsg(e?.payload?.detail ?? "Erro ao publicar."),
  });
  const createMut = useMutation({
    mutationFn: () => api.post(createPath, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invalidateKey });
      // Tool faz parte do snapshot publicado → sinaliza "há alterações não publicadas".
      if (!templateMode) qc.invalidateQueries({ queryKey: ["agent", agentId] });
      onClose();
    },
    onError: saveError,
  });

  const updateMut = useMutation({
    mutationFn: () => api.patch(updatePath, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invalidateKey });
      // Tool faz parte do snapshot publicado → sinaliza "há alterações não publicadas".
      if (!templateMode) qc.invalidateQueries({ queryKey: ["agent", agentId] });
      onClose();
    },
    onError: saveError,
  });

  const [guideCopied, setGuideCopied] = useState(false);
  const [testOut, setTestOut] = useState<{ result: string; tokens: Record<string, number>; logs: string } | null>(null);
  // Popup pré-teste: valores que faltam pra rodar (variáveis globais vazias + params
  // do Agente, que numa execução real o agente preencheria).
  const [varFill, setVarFill] = useState<{ vars: Record<string, string>; params: Record<string, string> } | null>(null);
  const testMut = useMutation({
    mutationFn: (opts: { vars: Record<string, string> | null; params: Record<string, string> | null }) =>
      api.post<{ result: string; tokens: Record<string, number>; logs: string; error: string | null }>(
        `/agents/${agentId}/tools/${tool.id}/test`,
        {
          sample_args: opts.params ?? {},
          code: form.kind === "python" ? form.code : null,
          prompts: form.kind === "python" ? buildPrompts(form) : null,
          param_values: form.kind === "python" ? (buildParamValues(form) ?? {}) : null,
          var_overrides: opts.vars,
        },
      ),
    onSuccess: (r) => setTestOut({ result: r.error ? `[erro] ${r.error}\n${r.result}` : r.result, tokens: r.tokens, logs: r.logs }),
  });

  // Pré-teste: junta o que falta pra rodar de verdade — variáveis globais vazias
  // (ex.: dependem de webhook) e os params do Agente (num teste não há agente real
  // pra preencher). Se algo falta, abre a popup; senão roda direto.
  async function startTest() {
    setTestOut(null);
    const agenteParams = form.kind === "python"
      ? scanRunParams(form.code).filter((p) => !form.fixoParams[p])
      : [];
    let emptyVars: string[] = [];
    const names = scanUsedVars(form);
    if (names.length) {
      try {
        const resolved = await api.post<Record<string, string>>(
          `/agents/${agentId}/tools/vars-preview`, { names },
        );
        emptyVars = names.filter((n) => !(resolved[n] ?? "").trim());
      } catch { /* preview falhou → segue sem pedir vars */ }
    }
    if (emptyVars.length || agenteParams.length) {
      setVarFill({
        vars: Object.fromEntries(emptyVars.map((n) => [n, ""])),
        params: Object.fromEntries(agenteParams.map((p) => [p, ""])),
      });
      return;
    }
    testMut.mutate({ vars: null, params: null });
  }

  const noBody = ["GET", "DELETE", "HEAD"].includes(form.method);
  const [confirmClose, setConfirmClose] = useState(false);
  const requestClose = () => { if (dirty) setConfirmClose(true); else onClose(); };
  // Headers não suportam campo IA (header HTTP é ASCII; valor livre da IA quebra). Só url/query/body.
  const aiNameCounts: Record<string, number> = {};
  for (const m of `${form.url}\n${form.params}\n${form.body}`.matchAll(/\[\[\s*(\w+)\s*\]\]/g)) {
    aiNameCounts[m[1]] = (aiNameCounts[m[1]] || 0) + 1;
  }
  function handleSave() {
    if (!form.name.trim()) { setFormError("Dê um nome à ferramenta."); return; }
    if (form.kind === "http" && !form.url.trim()) { setFormError("URL é obrigatória para tools HTTP."); return; }
    if (form.kind === "python" && !form.code.trim()) { setFormError("O código Python é obrigatório."); return; }
    setFormError("");
    if (globalMode && tool && !noteOpen) { setNoteOpen(true); return; }
    (tool ? updateMut : createMut).mutate();
  }

  return (
    <div
      {...backdropClose(() => { requestClose(); }, { guarded: true })}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--overlay)",
        zIndex: "var(--z-modal)" as any,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "var(--surface-hover)",
          border: "1px solid var(--line)",
          borderRadius: 12,
          width: 960,
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 24,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>
            {step === "curl" ? "Importar cURL" : tool ? "Editar ferramenta" : "Nova ferramenta"}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {canPublish && step !== "curl" && (
              <>
                {pubMsg && (
                  <span style={{ fontSize: 11, color: pubMsg.startsWith("✓") ? "var(--brand)" : "var(--danger)" }}>{pubMsg}</span>
                )}
                <button
                  title="Copia esta tool para o catálogo de Modelos de Tool do sistema"
                  disabled={publishMut.isPending}
                  onClick={() => { setPubMsg(""); publishMut.mutate(); }}
                  style={{
                    background: "none", border: "1px solid var(--line-strong)", borderRadius: 8,
                    padding: "5px 12px", fontSize: 12, color: "var(--ink-secondary)", cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}
                >
                  Publicar como modelo
                </button>
              </>
            )}
          <button
            onClick={requestClose}
            aria-label="Fechar"
            style={{ color: "var(--ink-muted)", background: "none", border: "none", fontSize: 16, cursor: "pointer" }}
          >
            <XIcon size={14} />
          </button>
          </div>
        </div>

        {step === "curl" && form.kind === "http" ? (
          <>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Cole a ferramenta copiada (JSON) ou um comando cURL</label>
              <textarea
                autoFocus
                value={curlText}
                onChange={(e) => { setCurlText(e.target.value); setCurlError(""); }}
                placeholder={'{ "name": "buscar_cliente", ... }\n\nou\n\ncurl -X POST https://api.example.com/...\n  -H "Authorization: Bearer TOKEN"\n  -d \'{"key": "value"}\''}
                style={{
                  ...inputStyle,
                  height: 140,
                  resize: "vertical",
                  fontFamily: "var(--font-mono)",
                  lineHeight: 1.5,
                }}
              />
              {curlError && <span style={{ fontSize: 12, color: "var(--brand)", marginTop: 6 }}>{curlError}</span>}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setStep("form")}
                style={{
                  background: "none",
                  color: "var(--ink-secondary)",
                  padding: "7px 14px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "1px solid var(--line-strong)",
                  cursor: "pointer",
                }}
              >
                Pular
              </button>
              <button
                onClick={handleImport}
                disabled={!curlText.trim()}
                style={{
                  background: "var(--brand)",
                  color: "#fff",
                  padding: "7px 14px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Preencher
              </button>
            </div>
          </>
        ) : (
          <>
            {pendingSecrets.length > 0 && (
              <div style={{ fontSize: 12, color: "var(--ink-secondary)", background: "var(--brand-soft)", border: "1px solid var(--brand)", borderRadius: 8, padding: "8px 12px" }}>
                Troque os <code>{SECRET_PLACEHOLDER}</code> pelos valores reais: {pendingSecrets.join(", ")}.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Nome</label>
              <input
                style={inputStyle}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="buscar_cliente"
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={labelStyle}>Descrição (para a IA)</label>
              <textarea
                rows={1}
                style={{ ...inputStyle, ...growStyle }}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Busca informações de um cliente pelo CPF"
              />
            </div>
            {templateMode && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Descrição do modelo (o que faz — vista na importação)</label>
                <textarea
                  value={form.template_description}
                  onChange={(e) => setForm({ ...form, template_description: e.target.value })}
                  placeholder="Explique para seu colega o que esta tool faz e quando importá-la."
                  spellCheck={false}
                  rows={1}
                  style={{ ...inputStyle, ...growStyle }}
                />
              </div>
            )}
            {globalMode && (
              <EditableFieldsPicker candidates={editableCandidates} value={form.editable_fields} onChange={(v) => setForm({ ...form, editable_fields: v })} />
            )}
            {form.kind === "http" && (
              <>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => { setCurlText(""); setCurlError(""); setCurlModal(true); }}
                    style={{ background: "none", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
                  >
                    Importar cURL
                  </button>
                </div>
                {curlModal && (
                  <div
                    {...backdropClose(() => { setCurlModal(false); })}
                    style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "calc(var(--z-modal) + 100)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <DialogPanel onClose={() => setCurlModal(false)} label="Importar cURL" style={{ background: "var(--surface-hover)", border: "1px solid var(--line-strong)", borderRadius: 10, padding: 20, width: 520, maxWidth: "92vw", display: "flex", flexDirection: "column", gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Importar cURL</span>
                      <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Substitui método, URL, headers, query e body desta ferramenta pelo cURL colado.</span>
                      <textarea
                        autoFocus
                        value={curlText}
                        onChange={(e) => { setCurlText(e.target.value); setCurlError(""); }}
                        placeholder={"curl -X POST https://api.exemplo.com/rota -H \"Authorization: Bearer XXX\" -d '{\"k\":\"v\"}'"}
                        style={{ ...inputStyle, height: 130, resize: "vertical", fontFamily: "var(--font-mono)", lineHeight: 1.5 }}
                      />
                      {curlError && <span style={{ fontSize: 12, color: "var(--brand)" }}>{curlError}</span>}
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button type="button" onClick={() => setCurlModal(false)} style={{ background: "none", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
                        <button type="button" onClick={importCurlOverwrite} disabled={!curlText.trim()} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>Importar</button>
                      </div>
                    </DialogPanel>
                  </div>
                )}
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", width: 110 }}>
                    <label style={labelStyle}>Método</label>
                    <select
                      style={inputStyle}
                      value={form.method}
                      onChange={(e) => setForm({ ...form, method: e.target.value })}
                    >
                      {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <label style={labelStyle}>URL</label>
                    <ExpressionEditor value={form.url} onChange={(v) => setForm({ ...form, url: v })} autoGrow minHeight={18} maxHeight={112} aiFields={aiDesc} onAiFieldsChange={setAiDesc} />
                  </div>
                </div>
                <SectionEditor key={`hdr-${curlImportKey}`} label="Headers" value={form.headers} onChange={(v) => setForm({ ...form, headers: v })} defaultMode="form" />
                <SectionEditor key={`qry-${curlImportKey}`} label="Query" value={form.params} onChange={(v) => setForm({ ...form, params: v })} defaultMode="form" aiFields={aiDesc} onAiFieldsChange={setAiDesc} aiNameCounts={aiNameCounts} />
                {!noBody && (
                  <SectionEditor key={`body-${curlImportKey}`} label="Body" value={form.body} onChange={(v) => setForm({ ...form, body: v })} defaultMode="form" aiFields={aiDesc} onAiFieldsChange={setAiDesc} aiNameCounts={aiNameCounts} />
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-secondary)" }}>
                    <input
                      type="checkbox"
                      checked={form.timeout !== null}
                      onChange={(e) => setForm({ ...form, timeout: e.target.checked ? 60 : null })}
                    />
                    Timeout personalizado {form.timeout === null && <span style={{ color: "var(--ink-muted)" }}>(padrão 60s)</span>}
                  </label>
                  {form.timeout !== null && (
                    <input
                      type="number"
                      min={1}
                      style={{ ...inputStyle, width: 140 }}
                      value={form.timeout}
                      onChange={(e) => setForm({ ...form, timeout: Number(e.target.value) || 1 })}
                    />
                  )}
                </div>
              </>
            )}
            {form.kind === "python" && (
              <>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Código Python — defina async def run(...)</label>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(TOOL_GUIDE);
                        setGuideCopied(true);
                        setTimeout(() => setGuideCopied(false), 1500);
                      }}
                      style={{ background: "none", color: guideCopied ? "#4caf50" : "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                      {guideCopied ? "✓ Copiado" : "Copiar guia p/ IA"}
                    </button>
                  </div>
                  <CodeEditor
                    value={form.code}
                    onChange={(v) => setForm({ ...form, code: v })}
                    minHeight={220}
                    maxHeight={320}
                    placeholder={"async def run(criterio: str = \"\", *, ctx):\n    # use prompts[\"chave\"] p/ prompts editaveis, e descreva os parametros nas caixas\n    return \"\""}
                  />
                  <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>
                    Precisa de ajuda? Use o botão <b>Copiar guia p/ IA</b> e peça o código pronto.
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-secondary)" }}>
                    <input
                      type="checkbox"
                      checked={form.timeout !== null}
                      onChange={(e) => setForm({ ...form, timeout: e.target.checked ? 60 : null })}
                    />
                    Timeout personalizado {form.timeout === null && <span style={{ color: "var(--ink-muted)" }}>(padrão 60s)</span>}
                  </label>
                  {form.timeout !== null && (
                    <input
                      type="number"
                      min={1}
                      style={{ ...inputStyle, width: 140 }}
                      value={form.timeout}
                      onChange={(e) => setForm({ ...form, timeout: Number(e.target.value) || 1 })}
                    />
                  )}
                </div>
                {scanRunParams(form.code).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>Parâmetros da Tool</span>
                      <span style={{ fontSize: 10, color: "var(--ink-dim)" }}>Agente = o agente que chama preenche · Fixo = valor fixo · aceita {"{{variáveis}}"}</span>
                    </div>
                    {scanRunParams(form.code).map((p) => {
                      const isFlat = !!form.fixoParams[p];
                      const nameEl = (
                        <span style={{ fontSize: 11, color: "var(--ink-secondary)", fontFamily: "var(--font-mono)" }}>{p}</span>
                      );
                      const seg = (
                        <div style={{ display: "flex", border: "1px solid var(--line-strong)", borderRadius: 6, overflow: "hidden" }}>
                          {([["Agente", false], ["Fixo", true]] as const).map(([lbl, flat]) => (
                            <button
                              key={lbl}
                              type="button"
                              onClick={() => {
                                // Toggle leva o texto junto (Fixo ↔ Agente) quando o destino está vazio.
                                const pv = { ...form.param_values }, pd = { ...form.paramDescriptions };
                                if (flat && !pv[p] && pd[p]) pv[p] = pd[p];
                                if (!flat && !pd[p] && pv[p]) pd[p] = pv[p];
                                setForm({ ...form, fixoParams: { ...form.fixoParams, [p]: flat }, param_values: pv, paramDescriptions: pd });
                              }}
                              style={{
                                fontSize: 10, padding: "2px 9px", cursor: "pointer", border: "none",
                                background: flat === isFlat ? "var(--brand)" : "transparent",
                                color: flat === isFlat ? "#fff" : "var(--ink-muted)",
                              }}
                            >
                              {lbl}
                            </button>
                          ))}
                        </div>
                      );
                      return isFlat ? (
                        <ExpressionEditor
                          key={p} autoGrow minHeight={22} maxHeight={112}
                          headerLeft={nameEl} headerActions={seg}
                          value={form.param_values[p] ?? ""}
                          onChange={(v) => setForm({ ...form, param_values: { ...form.param_values, [p]: v } })}
                        />
                      ) : (
                        <ExpressionEditor
                          key={p} autoGrow minHeight={22} maxHeight={112}
                          headerLeft={nameEl} headerActions={seg}
                          value={form.paramDescriptions[p] ?? ""}
                          onChange={(v) => setForm({ ...form, paramDescriptions: { ...form.paramDescriptions, [p]: v } })}
                        />
                      );
                    })}
                  </div>
                )}
                {scanPromptKeys(form.code).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>Parâmetros do código</span>
                      <span style={{ fontSize: 10, color: "var(--ink-dim)" }}>chamadas de IA no código via ai(prompts["chave"]) · aceita {"{{variáveis}}"}</span>
                    </div>
                    {scanPromptKeys(form.code).map((k) => (
                      <ExpressionEditor
                        key={`prompt-${k}`}
                        autoGrow minHeight={22} maxHeight={112}
                        headerLeft={<span style={{ fontSize: 11, color: "var(--ink-secondary)", fontFamily: "var(--font-mono)" }}>{k}</span>}
                        headerActions={<span style={{ fontSize: 10, fontWeight: 600, color: "var(--brand)", padding: "1px 8px", borderRadius: 99, background: "rgba(108,71,255,0.14)" }}>IA</span>}
                        value={form.prompts[k] ?? ""}
                        onChange={(v) => setForm({ ...form, prompts: { ...form.prompts, [k]: v } })}
                      />
                    ))}
                  </div>
                )}
                {showModelSelector && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <label style={labelStyle}>Modelo para o ai() desta ferramenta</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          model_provider_override: null,
                          model_name_override: null,
                          model_temperature_override: null,
                          model_reasoning_effort_override: null,
                        })}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          borderRadius: 8,
                          fontSize: 13,
                          cursor: "pointer",
                          border: `1px solid ${overrideOn ? "var(--line-strong)" : "var(--brand)"}`,
                          background: overrideOn ? "var(--surface-input)" : "var(--brand-soft)",
                          color: "var(--ink)",
                        }}
                      >
                        Usar modelo do Agente
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (overrideOn) return;
                          const first = modelCatalog[0];
                          if (!first) return;  // catálogo vazio/carregando → não comita estado meio-setado
                          setForm({
                            ...form,
                            model_provider_override: first.provider,
                            model_name_override: first.model_name,
                            model_temperature_override: 0.7,
                            model_reasoning_effort_override: null,
                          });
                        }}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          borderRadius: 8,
                          fontSize: 13,
                          cursor: "pointer",
                          border: `1px solid ${overrideOn ? "var(--brand)" : "var(--line-strong)"}`,
                          background: overrideOn ? "var(--brand-soft)" : "var(--surface-input)",
                          color: "var(--ink)",
                        }}
                      >
                        Escolher outro
                      </button>
                    </div>
                    {overrideOn && (
                      <>
                        <ModelSelector
                          compact
                          showMaxTokens={false}
                          value={{
                            provider: form.model_provider_override ?? "",
                            model_name: form.model_name_override ?? "",
                            temperature: form.model_temperature_override ?? 0.7,
                            reasoning_effort: form.model_reasoning_effort_override ?? "off",
                          }}
                          onChange={(next) =>
                            setForm({
                              ...form,
                              model_provider_override: next.provider,
                              model_name_override: next.model_name,
                              model_temperature_override: next.temperature,
                              model_reasoning_effort_override: next.reasoning_effort === "off" ? null : next.reasoning_effort,
                            })
                          }
                        />
                        <ModelFallbackSection
                          value={form.model_fallback}
                          onChange={(next) => setForm({ ...form, model_fallback: next })}
                          hint="Opcional. Usado se o modelo acima falhar."
                        />
                      </>
                    )}
                    <span style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.6 }}>
                      Por padrão, o <code>ai()</code> usa o modelo do agente — e o fallback dele junto. Escolha outro modelo (mais barato) só para a análise interna desta ferramenta.
                    </span>
                  </div>
                )}
              </>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={labelStyle}>Limite de uso (por cliente)</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select
                  style={{ ...inputStyle, width: 220 }}
                  value={form.cooldown_seconds == null ? "none" : form.cooldown_seconds < 0 ? "flat" : "cooldown"}
                  onChange={(e) => {
                    const m = e.target.value;
                    setForm({ ...form, cooldown_seconds: m === "none" ? null : m === "flat" ? -1 : 3600 });
                  }}
                >
                  <option value="none">Sem limite</option>
                  <option value="flat">Número fixo de vezes por cliente</option>
                  <option value="cooldown">A cada N segundos</option>
                </select>
                {form.cooldown_seconds != null && form.cooldown_seconds < 0 && (
                  <>
                    <input
                      type="number"
                      min={1}
                      style={{ ...inputStyle, width: 120 }}
                      value={-form.cooldown_seconds}
                      onChange={(e) => setForm({ ...form, cooldown_seconds: -Math.max(1, Number(e.target.value) || 1) })}
                    />
                    <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>vezes por cliente</span>
                  </>
                )}
                {form.cooldown_seconds != null && form.cooldown_seconds > 0 && (
                  <>
                    <input
                      type="number"
                      min={1}
                      style={{ ...inputStyle, width: 120 }}
                      value={form.cooldown_seconds}
                      onChange={(e) => setForm({ ...form, cooldown_seconds: Math.max(1, Number(e.target.value) || 1) })}
                    />
                    <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>segundos</span>
                  </>
                )}
              </div>
              {form.cooldown_seconds != null && (
                <textarea
                  value={form.cooldown_message}
                  onChange={(e) => setForm({ ...form, cooldown_message: e.target.value })}
                  placeholder="Mensagem que o agente recebe quando a tool está bloqueada (ex.: já busquei isso, use os dados anteriores). Vazio = padrão."
                  spellCheck={false}
                  rows={1}
                  style={{ ...inputStyle, ...growStyle }}
                />
              )}
              {form.cooldown_seconds != null && (
                <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                  Quando bloqueada, a tool devolve essa mensagem ao agente — ele segue a conversa, sem travar.
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#26262E", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.qualifies_human}
                  onChange={(e) => setForm({ ...form, qualifies_human: e.target.checked })}
                />
                Estratégica: qualifica lead para humano
              </label>
              <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                Quando esta tool for chamada com sucesso (fora de timeout), o cliente conta como "qualificado para humano" nas métricas.
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#26262E", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.ends_followup}
                  onChange={(e) => setForm({ ...form, ends_followup: e.target.checked })}
                />
                Esta tool encerra o follow-up
              </label>
              <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                Quando esta tool for chamada com sucesso, o follow-up desse lead é finalizado.
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle}>Pausar IA após responder</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="number"
                  min={0}
                  max={1440}
                  placeholder="Desligado"
                  style={{ ...inputStyle, width: 120 }}
                  value={form.pause_minutes ?? ""}
                  onChange={(e) => setForm({ ...form, pause_minutes: Math.min(1440, Math.max(0, Number(e.target.value) || 0)) || null })}
                />
                <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>minutos</span>
              </div>
              <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                Quando esta tool for chamada com sucesso, a resposta é enviada e a IA para de responder esse cliente por esse tempo (ex.: despedida). Mensagens nesse intervalo ficam como contexto. Vazio = desligado.
              </span>
            </div>
            {tool && !templateMode && form.kind === "python" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                <button
                  type="button"
                  onClick={() => startTest()}
                  disabled={testMut.isPending}
                  style={{ alignSelf: "flex-start", background: "none", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}
                >
                  {testMut.isPending ? "Testando..." : "Testar"}
                </button>
                {varFill && (
                  <div
                    {...backdropClose(() => { setVarFill(null); })}
                    style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "calc(var(--z-modal) + 100)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <DialogPanel onClose={() => setVarFill(null)} label="Valores para o teste" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 20, width: 440, maxWidth: "92vw", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>Valores para o teste</span>
                      {Object.keys(varFill.params).length > 0 && (
                        <>
                          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                            Parâmetros que o <b>agente</b> preencheria numa conversa real — informe um valor de teste:
                          </span>
                          {Object.keys(varFill.params).map((p) => (
                            <div key={p} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontSize: 11, color: "var(--ink-secondary)", fontFamily: "var(--font-mono)" }}>{p}</span>
                              <input
                                value={varFill.params[p]}
                                onChange={(e) => setVarFill({ ...varFill, params: { ...varFill.params, [p]: e.target.value } })}
                                placeholder={`Valor de teste para "${p}"`}
                                style={inputStyle}
                              />
                            </div>
                          ))}
                        </>
                      )}
                      {Object.keys(varFill.vars).length > 0 && (
                        <>
                          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                            Variáveis globais vazias agora (ex.: dependem de webhook) — só para este teste:
                          </span>
                          {Object.keys(varFill.vars).map((n) => (
                            <div key={n} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <span style={{ fontSize: 11, color: "var(--ink-secondary)", fontFamily: "var(--font-mono)" }}>{`{{${n}}}`}</span>
                              <input
                                value={varFill.vars[n]}
                                onChange={(e) => setVarFill({ ...varFill, vars: { ...varFill.vars, [n]: e.target.value } })}
                                placeholder={`Valor de teste para {{${n}}}`}
                                style={inputStyle}
                              />
                            </div>
                          ))}
                        </>
                      )}
                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          onClick={() => setVarFill(null)}
                          style={{ background: "none", color: "var(--ink-secondary)", border: "none", padding: "7px 14px", fontSize: 13, cursor: "pointer" }}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => { const v = varFill; setVarFill(null); testMut.mutate({ vars: v.vars, params: v.params }); }}
                          style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}
                        >
                          OK, testar
                        </button>
                      </div>
                    </DialogPanel>
                  </div>
                )}
                {testOut && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>Resultado</div>
                      <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-strong)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>{testOut.result}</pre>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", fontFamily: "var(--font-mono)" }}>
                      tokens: prompt {testOut.tokens.prompt ?? 0} / completion {testOut.tokens.completion ?? 0} / reasoning {testOut.tokens.reasoning ?? 0}
                    </div>
                    {testOut.logs && (
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>Logs</div>
                        <pre style={{ margin: 0, fontSize: 11, color: "var(--ink-mid)", whiteSpace: "pre-wrap", fontFamily: "var(--font-mono)", lineHeight: 1.6 }}>{testOut.logs}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center" }}>
              {formError ? (
                <span style={{ color: "var(--brand)", fontSize: 12, marginRight: "auto" }}>{formError}</span>
              ) : overrideMissingKey ? (
                <span style={{ color: "var(--warn)", fontSize: 12, marginRight: "auto" }}>
                  Sem chave API cadastrada para o provider "{form.model_provider_override}". A ferramenta vai falhar até cadastrar a chave.
                </span>
              ) : null}
              {!tool && initialStep === "curl" && (
                <button
                  onClick={() => setStep("curl")}
                  style={{
                    background: "none",
                    color: "var(--ink-secondary)",
                    padding: "7px 14px",
                    borderRadius: 8,
                    fontSize: 13,
                    border: "1px solid var(--line-strong)",
                    cursor: "pointer",
                  }}
                >
                  Voltar
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={createMut.isPending || updateMut.isPending}
                style={{
                  background: "var(--brand)",
                  color: "#fff",
                  padding: "7px 14px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {createMut.isPending || updateMut.isPending ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </>
        )}
      </div>
      {noteOpen && (
        <div {...backdropClose(() => { setNoteOpen(false); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setNoteOpen(false)} label="Nova versão da tool global" style={{ background: "var(--surface-app)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 460, display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Nova versão da tool global</span>
            <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>Salvar vale <strong>na hora</strong> em todo agente vinculado. Registre o que mudou (fica no histórico de versões).</span>
            <textarea autoFocus rows={3} value={versionNote} onChange={(e) => setVersionNote(e.target.value)} placeholder="Ex.: URL nova do estoque e timeout maior" style={{ ...inputStyle, resize: "vertical" }} />
            {formError && <span style={{ fontSize: 12, color: "var(--danger)" }}>{formError}</span>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setNoteOpen(false)} style={{ padding: "7px 14px", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer" }}>Cancelar</button>
              <button type="button" onClick={handleSave} disabled={!versionNote.trim() || updateMut.isPending} style={{ padding: "7px 14px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer", opacity: !versionNote.trim() || updateMut.isPending ? 0.6 : 1 }}>
                {updateMut.isPending ? "Salvando..." : "Salvar nova versão"}
              </button>
            </div>
          </DialogPanel>
        </div>
      )}
      {confirmClose && (
        <ConfirmDialog
          title="Descartar alterações?"
          message="Você vai perder o que preencheu nesta ferramenta."
          confirmLabel="Descartar"
          danger
          onConfirm={onClose}
          onCancel={() => setConfirmClose(false)}
        />
      )}
    </div>
  );
}

// Navegador de modelos de tools (tools do agente-sentinela) → importa uma cópia
// pro agente atual. Busca por nome; tooltip nativo mostra a descrição.
export function TemplatePickerModal({ agentId, onClose, role = "agent", channel = "whatsapp" }: { agentId: string; onClose: () => void; role?: "agent" | "followup_end"; channel?: Channel }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [importError, setImportError] = useState("");

  const { data: templates = [], isLoading } = useQuery<any[]>({
    // Banco de modelos é único: a tool de encerramento importa de qualquer modelo
    // (o role vai no import, não vem do template).
    queryKey: ["tool-templates"],
    queryFn: () => api.get("/tool-templates"),
  });

  const importMut = useMutation({
    mutationFn: (tplId: string) => api.post(`/agents/${agentId}/tools/import`, { template_tool_id: tplId, role, channel }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: role === "followup_end" ? ["agent-tools", agentId, "followup_end"] : ["agent-tools", agentId] }); qc.invalidateQueries({ queryKey: ["agent", agentId] }); onClose(); },
    onError: (e: any) => setImportError(e?.payload?.detail ?? "Erro ao importar."),
  });

  const q = search.trim().toLowerCase();
  // Template de estoque global (editable_fields) é importável: vira tool vinculada, listada na sub-aba Estoque.
  const importables = templates.filter((t) => t.kind !== "estoque" || Array.isArray(t.editable_fields));
  const filtered = q ? importables.filter((t) => (t.name ?? "").toLowerCase().includes(q)) : importables;

  return (
    <div
      {...backdropClose(() => { onClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <DialogPanel onClose={() => onClose()} label="Importar modelo de tool" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 560, maxWidth: "95vw", maxHeight: "85vh", padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>Importar modelo de tool</span>
          <button aria-label="Fechar" onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome..." style={inputStyle} />
        {importError && <span style={{ fontSize: 12, color: "var(--brand)" }}>{importError}</span>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, overflowY: "auto" }}>
          {isLoading ? (
            <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>Carregando…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: "var(--ink-muted)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>
              {importables.length === 0 ? "Nenhum modelo de tool cadastrado." : "Nenhum modelo encontrado."}
            </div>
          ) : (
            filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                title={t.template_description || t.description || ""}
                disabled={importMut.isPending}
                onClick={() => { setImportError(""); importMut.mutate(t.id); }}
                style={{ textAlign: "left", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; }}
              >
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}>
                  {t.name}{Array.isArray(t.editable_fields) && <span style={globalTag}>Global</span>}<VersionBadge versions={t.versions} />
                </span>
                <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-soft)", border: "1px solid var(--line-strong)", borderRadius: 6, padding: "2px 7px" }}>
                  {t.kind === "python" ? "Python" : "HTTP"}
                </span>
              </button>
            ))
          )}
        </div>
      </DialogPanel>
    </div>
  );
}

export function ConfigTools({ agent, channel = "whatsapp" }: { agent: any; channel?: Channel }) {
  const [picker, setPicker] = useState(false);
  const [creating, setCreating] = useState<{ kind: "http" | "python"; step: "curl" | "form" } | null>(null);
  const [importing, setImporting] = useState(false);
  const [editingTool, setEditingTool] = useState<any | null>(null);
  const [delTool, setDelTool] = useState<any | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const qc = useQueryClient();
  // Só as tools da sub-aba: cada uma roda apenas em execução do próprio canal.
  const { data: allTools = [] } = useQuery<any[]>({
    queryKey: ["agent-tools", agent.id, "agent", channel],
    queryFn: () => api.get(`/agents/${agent.id}/tools?channel=${channel}`),
  });
  // Tools de estoque têm seção própria (sub-aba Estoque) — fora da lista geral.
  const tools = allTools.filter((t: any) => t.kind !== "estoque");
  const deleteMut = useMutation({
    mutationFn: (toolId: string) => api.delete(`/agents/${agent.id}/tools/${toolId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-tools", agent.id] }); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
  });
  const pauseMut = useMutation({
    mutationFn: ({ toolId, enabled }: { toolId: string; enabled: boolean }) =>
      api.patch(`/agents/${agent.id}/tools/${toolId}`, { enabled }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-tools", agent.id] }); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
  });
  const dupMut = useMutation({
    mutationFn: (toolId: string) => api.post(`/agents/${agent.id}/tools/${toolId}/duplicate`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent-tools", agent.id] }); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {agent.template_id && <GlobalToolsPanel agent={agent} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>
          {tools.length} ferramenta{tools.length !== 1 ? "s" : ""} configurada{tools.length !== 1 ? "s" : ""}
        </span>
        <button
          onClick={() => setPicker(true)}
          style={{
            background: "var(--brand)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "7px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          + Nova ferramenta
        </button>
      </div>

      {tools.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            color: "var(--line-max)",
            fontSize: 13,
            border: "1px dashed var(--line)",
            borderRadius: 8,
          }}
        >
          Nenhuma ferramenta configurada ainda.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tools.map((tool: any) => (
            <div
              key={tool.id}
              onClick={() => setEditingTool(tool)}
              style={{
                background: "var(--surface-hover)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "12px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0, opacity: tool.enabled === false ? 0.5 : 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}>
                  {tool.name}
                  {tool.template_id && <span style={globalTag}>Tool global</span>}
                  {tool.template_id && <VersionBadge versions={tool.template_versions} />}
                  {tool.enabled === false && (
                    <span style={{ fontSize: 10, fontWeight: 500, padding: "1px 7px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-muted)" }}>
                      pausada
                    </span>
                  )}
                </div>
                {tool.description && (
                  <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>{tool.description}</div>
                )}
                <div style={{ fontSize: 11, color: "var(--line-max)", marginTop: 6, fontFamily: "var(--font-mono)" }}>
                  {tool.template_id ? `Tool global · ${tool.kind === "python" ? "Python" : "HTTP"}` : tool.kind === "python" ? "Python" : `${tool.method ?? ""} ${tool.url ?? ""}`}
                </div>
              </div>
              {!tool.template_id && (
                <button
                  title={copiedId === tool.id ? "Copiado!" : "Copiar (cole em Nova ferramenta → Colar; chaves viram <id ou key>)"}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(toolToClipboard(tool)).then(() => {
                      setCopiedId(tool.id);
                      setTimeout(() => setCopiedId((id) => (id === tool.id ? null : id)), 1500);
                    }, () => toastError("Não consegui copiar: o navegador bloqueou a área de transferência."));
                  }}
                  style={{ background: "none", border: "none", color: copiedId === tool.id ? "var(--brand)" : "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = copiedId === tool.id ? "var(--brand)" : "var(--ink-muted)")}
                >
                  {copiedId === tool.id ? (
                    <CheckIcon size={14} />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>
                  )}
                </button>
              )}
              <button
                title="Duplicar (cria cópia pausada)"
                disabled={dupMut.isPending}
                onClick={(e) => { e.stopPropagation(); dupMut.mutate(tool.id); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-muted)")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
              </button>
              <button
                title={tool.enabled === false ? "Reativar ferramenta" : "Pausar ferramenta (o agente perde acesso)"}
                disabled={pauseMut.isPending}
                onClick={(e) => { e.stopPropagation(); pauseMut.mutate({ toolId: tool.id, enabled: tool.enabled === false }); }}
                style={{ background: "none", border: "none", color: tool.enabled === false ? "var(--brand)" : "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = tool.enabled === false ? "var(--brand)" : "var(--ink-muted)")}
              >
                {tool.enabled === false ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                )}
              </button>
              <button
                title="Excluir ferramenta"
                disabled={deleteMut.isPending}
                onClick={(e) => { e.stopPropagation(); setDelTool(tool); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-muted)")}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {picker && (
        <div
          {...backdropClose(() => { setPicker(false); })}
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <DialogPanel onClose={() => setPicker(false)} label="Nova ferramenta" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 12, width: 360 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Nova ferramenta</span>
            <div style={{ display: "flex", gap: 10 }}>
              {([
                ["Importar", { kind: "http", step: "curl" }],
                ["Colar", { kind: "http", step: "curl" }],
                ["HTTP", { kind: "http", step: "form" }],
                ["Python", { kind: "python", step: "form" }],
              ] as const).map(([label, cfg]) => (
                <button
                  key={label}
                  onClick={() => {
                    setPicker(false);
                    if (label === "Importar") setImporting(true);
                    else setCreating(cfg);
                  }}
                  style={{ flex: 1, background: "var(--surface-input)", color: "var(--ink)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "16px 0", fontSize: 13, cursor: "pointer", transition: "border-color .12s, background .12s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.background = "var(--brand-soft)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.background = "var(--surface-input)"; }}
                >
                  {label}
                </button>
              ))}
            </div>
          </DialogPanel>
        </div>
      )}

      {creating && (
        <ToolModal
          agentId={agent.id}
          initialKind={creating.kind}
          initialStep={creating.step}
          channel={channel}
          onClose={() => setCreating(null)}
        />
      )}
      {editingTool && (editingTool.template_id
        ? <GlobalToolEditor key={editingTool.id} agentId={agent.id} tool={editingTool} onClose={() => setEditingTool(null)} />
        : <ToolModal key={editingTool.id} agentId={agent.id} tool={editingTool} onClose={() => setEditingTool(null)} />
      )}
      {importing && (
        <TemplatePickerModal agentId={agent.id} channel={channel} onClose={() => setImporting(false)} />
      )}
      {delTool && (
        <ConfirmDialog
          title={`Excluir "${delTool.name}"?`}
          confirmLabel="Excluir"
          danger
          onConfirm={() => { deleteMut.mutate(delTool.id); setDelTool(null); }}
          onCancel={() => setDelTool(null)}
        />
      )}
    </div>
  );
}
